import type { ITourStepAction } from '@srm/core/contracts/tours';

/**
 * Проверяет соответствие маршрута паттерну (регулярное выражение).
 *
 * `route` — это `pathname + hash` (см. useCurrentRoute), что позволяет таргетить
 * туры не только на страницу, но и на конкретную вкладку карточки документа,
 * адресуемую хэшем `#page-<blockId>`.
 *
 * Примеры routePattern:
 *   ^/document/                                   — все карточки документов
 *   ^/(document|task)/                            — документы и задачи
 *   ^/documents$                                  — только реестр документов
 *   #page-tlc(CustomWinnerChoice|WinnerChoiceDemandLots) — вкладки «Выбор победителя»
 *   null / ''                                     — любой маршрут
 */
export function matchRoutePattern(routePattern: string | null | undefined, route: string): boolean {
    if (routePattern == null || routePattern === '') return true;
    try {
        return new RegExp(routePattern).test(route);
    } catch {
        return false;
    }
}

/**
 * Ожидает выполнения действия пользователя на DOM-элементе.
 *
 * Возвращает Promise, который resolve-ится при наступлении события.
 * Используется как before-hook в react-joyride для блокировки перехода
 * на следующий шаг до выполнения действия.
 *
 * @param action — конфигурация ожидаемого действия
 * @param defaultTarget — CSS-селектор шага (fallback, если action.target не задан)
 * @param signal — AbortSignal для отмены ожидания при размонтировании
 */
const WAIT_FOR_ELEMENT_INTERVAL = 300;
const WAIT_FOR_ELEMENT_TIMEOUT = 5000;

export function waitForAction(
    action: ITourStepAction,
    defaultTarget: string,
    signal?: AbortSignal,
): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        if (signal?.aborted) {
            reject(new DOMException('Aborted', 'AbortError'));
            return;
        }

        const selector = action.target || defaultTarget;
        const eventType = action.type;

        let intervalId: ReturnType<typeof setInterval> | null = null;
        let listenerAttached = false;

        const cleanup = () => {
            if (intervalId !== null) { clearInterval(intervalId); intervalId = null; }
            if (listenerAttached) {
                document.removeEventListener(eventType, handler, true);
                listenerAttached = false;
            }
            signal?.removeEventListener('abort', onAbort);
        };

        // Event delegation на document: ловим событие в capture-фазе и проверяем,
        // что event.target лежит внутри selector-а. Это надёжно для любой вложенности
        // (например, клик по <span> внутри <button>, или по кнопке внутри модалки),
        // и не зависит от того, какой конкретно узел вернул querySelector первым.
        const handler = (e: Event) => {
            const target = e.target;
            if (!(target instanceof Element)) return;
            if (!target.closest(selector)) return;

            if (eventType === 'input' || eventType === 'change') {
                if (action.value) {
                    const val = (e.target as HTMLInputElement).value;
                    try {
                        if (!new RegExp(action.value).test(val)) return;
                    } catch {
                        if (val !== action.value) return;
                    }
                }
            }
            cleanup();
            resolve();
        };

        const onAbort = () => {
            cleanup();
            reject(new DOMException('Aborted', 'AbortError'));
        };

        const attachListener = () => {
            if (listenerAttached) return;
            document.addEventListener(eventType, handler, true);
            listenerAttached = true;
            if (intervalId !== null) { clearInterval(intervalId); intervalId = null; }
        };

        // Пытаемся найти элемент сразу
        if (document.querySelector(selector)) {
            attachListener();
        } else {
            // Элемент не найден — поллим до появления или таймаута
            const started = Date.now();
            intervalId = setInterval(() => {
                if (document.querySelector(selector)) {
                    attachListener();
                } else if (Date.now() - started > WAIT_FOR_ELEMENT_TIMEOUT) {
                    cleanup();
                    reject(new DOMException('Timeout waiting for action target', 'TimeoutError'));
                }
            }, WAIT_FOR_ELEMENT_INTERVAL);
        }

        signal?.addEventListener('abort', onAbort, { once: true });
    });
}

/** Ключ sessionStorage для мультистраничных туров */
export const PENDING_TOUR_KEY = 'tours:pending';

/** TTL для pending-тура (5 минут) */
export const PENDING_TOUR_TTL = 5 * 60 * 1000;

/** Данные, сохраняемые между страницами для мультистраничного тура */
export interface IPendingTour {
    tourId: string;
    stepIndex: number;
    stepHistory: number[];
    timestamp: number;
}

/** Сохраняет состояние тура в sessionStorage перед навигацией */
export function savePendingTour(tourId: string, stepIndex: number, stepHistory: number[] = []): void {
    const data: IPendingTour = { tourId, stepIndex, stepHistory, timestamp: Date.now() };
    sessionStorage.setItem(PENDING_TOUR_KEY, JSON.stringify(data));
}

/** Извлекает и удаляет pending-тур из sessionStorage. Возвращает null если просрочен или отсутствует. */
export function consumePendingTour(): IPendingTour | null {
    const raw = sessionStorage.getItem(PENDING_TOUR_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PENDING_TOUR_KEY);
    try {
        const data: IPendingTour = JSON.parse(raw);
        if (Date.now() - data.timestamp > PENDING_TOUR_TTL) return null;
        return data;
    } catch {
        return null;
    }
}
